/**
 * Purchasing Activity read model — client-side projection across
 * purchase_orders, shopping_lists, count_sessions, and purchase_allocations.
 *
 * No new tables; this is a presentation layer that classifies existing
 * domain records into lifecycle stages:
 *   needs_attention → ready → in_progress → completed
 *
 * Each activity has a stable shape for the Purchasing workspace cards:
 *   { id, kind, stage, vendor_id, vendor_name, title, subtitle,
 *     item_count, amount, amount_kind, created_at, updated_at,
 *     created_by_name, created_by_user_id,
 *     source_count_session_id, purchasing_plan_id, purchase_order_id, shopping_list_id,
 *     problem_type, next_action }
 *
 * `created_by_name` is whatever name the record already carries; when it only
 * carries a user id, `created_by_user_id` lets the caller resolve a name once
 * and reuse it across cards rather than every card fetching its own.
 */

export const ACTIVITY_KIND = Object.freeze({
  COUNT: 'count',
  SAVINGS_REVIEW: 'savings_review',
  PURCHASE_ORDER: 'purchase_order',
  SHOPPING_LIST: 'shopping_list',
});

export const ACTIVITY_STAGE = Object.freeze({
  NEEDS_ATTENTION: 'needs_attention',
  READY: 'ready',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
});

export const AMOUNT_KIND = Object.freeze({
  ESTIMATED: 'estimated',
  ORDERED: 'ordered',
  ACTUAL: 'actual',
  VERIFIED: 'verified',
});

// PO statuses that count as "sent family" — beyond planning, awaiting delivery
const SENT_FAMILY = new Set([
  'sent', 'delivered', 'opened', 'clicked', 'sending',
  'pending_approval', 'confirmed', 'delivery_delayed',
]);

const COMPLETED_PO_STATUSES = new Set(['received', 'closed', 'canceled', 'replaced', 'voided', 'archived']);

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function fmtCurrency(amount) {
  const n = toNumber(amount);
  return n;
}

function derivePoStage(po, attentionIds) {
  if (!po) return ACTIVITY_STAGE.READY;
  const id = String(po.id);
  if (attentionIds.has(id)) return ACTIVITY_STAGE.NEEDS_ATTENTION;
  const status = po.delivery_status || '';
  if (status === 'draft' || status === 'revised') return ACTIVITY_STAGE.READY;
  if (SENT_FAMILY.has(status)) return ACTIVITY_STAGE.IN_PROGRESS;
  if (COMPLETED_PO_STATUSES.has(status) || status === 'received' || status === 'closed') return ACTIVITY_STAGE.COMPLETED;
  if (status === 'blocked_no_email' || status === 'bounced' || status === 'failed' || status === 'blocked' || status === 'error' || status === 'complained') {
    return ACTIVITY_STAGE.NEEDS_ATTENTION;
  }
  // default sent-ish
  return ACTIVITY_STAGE.IN_PROGRESS;
}

export function classifyPurchaseOrderStage(po, attentionIds = new Set()) {
  return derivePoStage(po, attentionIds);
}

function buildAttentionSet(problems) {
  const ids = new Set();
  if (!problems) return ids;
  const buckets = [problems.staleDrafts, problems.bouncedOrFailed, problems.missingEmail, problems.stalledPOs];
  buckets.forEach((arr) => {
    (arr || []).forEach((po) => { if (po?.id) ids.add(String(po.id)); });
  });
  return ids;
}

export function poToActivity(po, { attentionIds = new Set() } = {}) {
  if (!po) return null;
  const stage = derivePoStage(po, attentionIds);
  const isAttention = stage === ACTIVITY_STAGE.NEEDS_ATTENTION;
  let problemType = null;
  if (isAttention) {
    // infer problem type from delivery_status
    const s = po.delivery_status;
    if (s === 'draft') problemType = 'stale_draft';
    else if (s === 'blocked_no_email') problemType = 'missing_email';
    else if (['bounced', 'complained', 'failed', 'error', 'blocked', 'delivery_delayed'].includes(s)) problemType = 'bounced_failed';
    else problemType = 'stalled';
  }
  const vendorName = po.vendor_name_snapshot || 'Unknown vendor';
  const title = vendorName;
  const subtitle = po.po_number ? `${po.po_number} · ${stageLabelForPo(po)}` : stageLabelForPo(po);
  const amountKind = stage === ACTIVITY_STAGE.READY ? AMOUNT_KIND.ESTIMATED : stage === ACTIVITY_STAGE.IN_PROGRESS ? AMOUNT_KIND.ORDERED : AMOUNT_KIND.VERIFIED;
  return {
    id: `po-${po.id}`,
    kind: ACTIVITY_KIND.PURCHASE_ORDER,
    stage,
    vendor_id: po.vendor_id || null,
    vendor_name: vendorName,
    title,
    subtitle,
    item_count: toNumber(po.line_count) || (Array.isArray(po.purchase_order_items) ? po.purchase_order_items.length : 0) || 0,
    amount: fmtCurrency(po.total),
    amount_kind: amountKind,
    created_at: po.created_at,
    updated_at: po.updated_at || po.sent_at || po.created_at,
    // A PO already records who placed it; nothing to resolve.
    created_by_name: po.ordered_by_name || null,
    created_by_user_id: po.ordered_by_user_id || null,
    source_count_session_id: po.source_count_session_id || null,
    purchasing_plan_id: null,
    purchase_order_id: po.id,
    shopping_list_id: null,
    problem_type: problemType,
    next_action: nextActionForPo(po, stage, problemType),
    raw: po,
  };
}

function stageLabelForPo(po) {
  const status = po.delivery_status || '';
  const map = {
    draft: 'Draft · needs review',
    blocked_no_email: 'Missing vendor email',
    bounced: 'Delivery failed',
    failed: 'Delivery failed',
    error: 'Delivery failed',
    blocked: 'Blocked',
    complained: 'Bounced',
    delivery_delayed: 'Delayed',
    sent: po.sent_at ? `Sent ${formatDateShort(po.sent_at)}` : 'Sent',
    delivered: 'Delivered',
    opened: 'Opened by vendor',
    clicked: 'Clicked by vendor',
    sending: 'Sending…',
    pending_approval: 'Pending approval',
    confirmed: 'Confirmed',
    received: 'Received',
    closed: 'Closed',
    canceled: 'Canceled',
    replaced: 'Replaced',
    voided: 'Voided',
  };
  return map[status] || status || 'Purchase order';
}

function nextActionForPo(po, stage, problemType) {
  if (stage === ACTIVITY_STAGE.NEEDS_ATTENTION) {
    if (problemType === 'stale_draft') return { label: 'Review draft', action: 'review_draft' };
    if (problemType === 'missing_email') return { label: 'Add email', action: 'add_email' };
    if (problemType === 'bounced_failed') return { label: 'Fix & resend', action: 'fix_resend' };
    if (problemType === 'stalled') return { label: 'Follow up', action: 'follow_up' };
    return { label: 'Review', action: 'review' };
  }
  if (stage === ACTIVITY_STAGE.READY) return { label: 'Review & send', action: 'review_draft' };
  if (stage === ACTIVITY_STAGE.IN_PROGRESS) return { label: 'View order', action: 'view' };
  return { label: 'View purchase', action: 'view' };
}

function formatDateShort(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return ''; }
}

// ---------------------------------------------------------------------------
// Shopping lists

export function shoppingListToActivity(list, { detail = null } = {}) {
  if (!list) return null;
  const status = list.status || 'open';
  const vendorName = list.vendor_name_snapshot || 'Pickup vendor';
  let stage;
  let subtitle;
  let nextAction;
  let amount = 0;
  let itemCount = list.item_count ?? 0;
  let amountKind = AMOUNT_KIND.ESTIMATED;

  if (detail && Array.isArray(detail.items)) {
    itemCount = detail.items.length;
    const estimated = detail.items.reduce((sum, it) => sum + (toNumber(it.estimated_line_total) || toNumber(it.packages) * toNumber(it.estimated_price)), 0);
    amount = estimated;
    if (status === 'completed') {
      const actual = detail.items.filter((i) => i.found_status === 'found').reduce((s, it) => s + (toNumber(it.actual_price) * toNumber(it.actual_quantity) || toNumber(it.estimated_line_total) || 0), 0);
      // If we have actuals, show actual; else estimated
      amount = actual || estimated;
      amountKind = actual ? AMOUNT_KIND.ACTUAL : AMOUNT_KIND.ESTIMATED;
    }
  } else if (list.total != null) {
    amount = toNumber(list.total);
  }

  if (status === 'completed' || status === 'canceled') {
    stage = ACTIVITY_STAGE.COMPLETED;
    subtitle = list.completed_at ? `Completed ${formatDateShort(list.completed_at)}` : 'Completed';
    nextAction = { label: 'View trip', action: 'view_trip' };
    amountKind = AMOUNT_KIND.ACTUAL;
  } else {
    // open — decide ready vs in_progress based on found_status if detail available
    if (detail && Array.isArray(detail.items)) {
      const hasProgress = detail.items.some((it) => it.found_status === 'found' || it.found_status === 'unavailable');
      if (hasProgress) {
        stage = ACTIVITY_STAGE.IN_PROGRESS;
        const found = detail.items.filter((i) => i.found_status === 'found').length;
        subtitle = `${found} of ${itemCount} reviewed · shopping`;
        nextAction = { label: 'Continue shopping', action: 'continue_shopping' };
        amountKind = AMOUNT_KIND.ESTIMATED;
      } else {
        stage = ACTIVITY_STAGE.READY;
        subtitle = `${itemCount} items · estimated`;
        nextAction = { label: 'Start shopping', action: 'start_shopping' };
      }
    } else {
      stage = ACTIVITY_STAGE.READY;
      subtitle = `${itemCount} items · estimated`;
      nextAction = { label: 'Start shopping', action: 'start_shopping' };
    }
  }

  return {
    id: `sl-${list.id}`,
    kind: ACTIVITY_KIND.SHOPPING_LIST,
    stage,
    vendor_id: list.vendor_id || null,
    vendor_name: vendorName,
    title: vendorName,
    subtitle,
    item_count: itemCount,
    amount,
    amount_kind: amountKind,
    created_at: list.created_at,
    updated_at: list.updated_at || list.completed_at || list.created_at,
    created_by_name: null,
    created_by_user_id: list.created_by || null,
    source_count_session_id: list.source_allocation_id || null,
    purchasing_plan_id: list.source_allocation_id || null,
    purchase_order_id: null,
    shopping_list_id: list.id,
    problem_type: null,
    next_action: nextAction,
    raw: list,
    detail,
  };
}

// ---------------------------------------------------------------------------
// Count sessions

export function countToActivity(session, { hubStatus = null, hasOpenPlan = false, hasDraft = false } = {}) {
  if (!session) return null;
  // If count already has an open savings review or a draft PO, the count itself
  // is not the work — the derived document is. Suppress to avoid duplicate cards.
  if (hasOpenPlan || hasDraft) return null;
  // If hub shows it was already sent/PO generated, don't surface as ready
  const everDone = hubStatus && (hubStatus.hasSend || hubStatus.hasGeneratePo);
  if (everDone) return null;

  const itemCount = session.total_items ?? session.items_counted ?? 0;
  const title = session.name || session.template_name_snapshot || 'Inventory count';
  const subtitle = session.completed_at ? `Completed ${formatDateShort(session.completed_at)}` : 'Completed count';
  return {
    id: `count-${session.id}`,
    kind: ACTIVITY_KIND.COUNT,
    stage: ACTIVITY_STAGE.READY,
    vendor_id: null,
    vendor_name: '',
    title,
    subtitle,
    item_count: itemCount,
    amount: 0,
    amount_kind: AMOUNT_KIND.ESTIMATED,
    created_at: session.completed_at || session.created_at,
    updated_at: session.completed_at || session.created_at,
    // count_sessions.counted_by is already a name; created_by is a user id.
    created_by_name: session.counted_by || null,
    created_by_user_id: session.created_by || null,
    source_count_session_id: session.id,
    purchasing_plan_id: null,
    purchase_order_id: null,
    shopping_list_id: null,
    problem_type: null,
    next_action: { label: 'Review purchases', action: 'review_count' },
    raw: session,
  };
}

export function savingsReviewToActivity(plan) {
  if (!plan) return null;
  const countName = plan.count_session_id ? `Count ${String(plan.count_session_id).slice(0, 8)}` : 'Purchase plan';
  // Prefer the plan's own created_at for ordering
  const title = plan.title || countName;
  const itemCount = plan.itemCount ?? (Array.isArray(plan.lines) ? plan.lines.length : 0) ?? 0;
  const vendorCount = plan.vendor_count ?? plan.vendorCount ?? null;
  const subtitle = vendorCount ? `${itemCount} products · ${vendorCount} vendors · Vendor comparison ready` : `${itemCount} products · Vendor comparison ready`;
  return {
    id: `plan-${plan.id}`,
    kind: ACTIVITY_KIND.SAVINGS_REVIEW,
    stage: ACTIVITY_STAGE.READY,
    vendor_id: null,
    vendor_name: '',
    title,
    subtitle,
    item_count: itemCount,
    amount: toNumber(plan.estimatedTotal ?? plan.estimated_total),
    amount_kind: AMOUNT_KIND.ESTIMATED,
    created_at: plan.created_at,
    updated_at: plan.updated_at || plan.created_at,
    created_by_name: plan.created_by_name || null,
    created_by_user_id: plan.created_by || plan.user_id || null,
    source_count_session_id: plan.count_session_id || null,
    purchasing_plan_id: plan.id,
    purchase_order_id: null,
    shopping_list_id: null,
    problem_type: null,
    next_action: { label: 'Open Purchase Planner', action: 'review_plan' },
    raw: plan,
  };
}

// ---------------------------------------------------------------------------
// Aggregator — builds stage buckets from raw collections

export function buildPurchasingWorkQueue({
  purchaseOrders = [],
  shoppingLists = [],
  shoppingListDetails = {}, // map shopping_list_id → detail with items
  countSessions = [],
  hubStatusMap = {},
  savingsPlanMap = {},
  draftPoMap = {},
  plans = [],
  problems = { staleDrafts: [], bouncedOrFailed: [], missingEmail: [], stalledPOs: [] },
} = {}) {
  const attentionIds = buildAttentionSet(problems);
  const activities = [];

  // POs → activities
  (purchaseOrders || []).forEach((po) => {
    const act = poToActivity(po, { attentionIds });
    if (act) activities.push(act);
  });

  // Shopping lists → activities (use detail when available for progress)
  (shoppingLists || []).forEach((list) => {
    const detail = shoppingListDetails[String(list.id)] || null;
    const act = shoppingListToActivity(list, { detail });
    if (act) activities.push(act);
  });

  // Plans (open savings reviews) → activities, deduplicated against count map
  // If a count maps to a plan, the plan wins — we do not also emit the count
  (plans || []).forEach((plan) => {
    // Avoid double-counting if this plan's count already has an activity via savingsPlanMap
    const act = savingsReviewToActivity(plan);
    if (act) activities.push(act);
  });

  // Counts that still need action → activities (suppressed when plan/draft exists)
  (countSessions || []).forEach((session) => {
    const sid = String(session.id);
    const hubStatus = hubStatusMap[sid] || null;
    const hasOpenPlan = !!savingsPlanMap[sid];
    const hasDraft = Array.isArray(draftPoMap[sid]) && draftPoMap[sid].length > 0;
    // If we already have a plan activity for this count, skip the count row
    if (hasOpenPlan) return;
    const act = countToActivity(session, { hubStatus, hasOpenPlan, hasDraft });
    if (act) activities.push(act);
  });

  // Bucket by stage
  const needsAttention = activities.filter((a) => a.stage === ACTIVITY_STAGE.NEEDS_ATTENTION)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  const ready = activities.filter((a) => a.stage === ACTIVITY_STAGE.READY)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  const inProgress = activities.filter((a) => a.stage === ACTIVITY_STAGE.IN_PROGRESS)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  const completed = activities.filter((a) => a.stage === ACTIVITY_STAGE.COMPLETED)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  // Recent purchases: most recent completed activities, capped at 10
  const recent = [...completed].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).slice(0, 10);

  return { activities, needsAttention, ready, inProgress, completed, recent, attentionIds };
}

// Helper for tests / UI — pure stage counts
export function summarizeWorkQueue(queue) {
  return {
    needsAttention: queue.needsAttention.length,
    ready: queue.ready.length,
    inProgress: queue.inProgress.length,
    completed: queue.completed.length,
    recent: queue.recent.length,
    totalActive: queue.needsAttention.length + queue.ready.length + queue.inProgress.length,
  };
}

// Build a single attention row view-model with what/why/what-to-do
export function buildAttentionRow(activity, po = null) {
  if (!activity) return null;
  const s = po?.delivery_status || activity.problem_type || '';
  if (s === 'stale_draft' || activity.problem_type === 'stale_draft') {
    return {
      what: `${activity.vendor_name || 'Draft order'} — stale draft`,
      why: 'This draft has been waiting over 48 hours and may be forgotten.',
      actionLabel: 'Review draft',
    };
  }
  if (s === 'blocked_no_email' || activity.problem_type === 'missing_email') {
    return {
      what: `${activity.vendor_name || 'Vendor'} — missing email`,
      why: 'No vendor email on file, so the order cannot be sent.',
      actionLabel: 'Add email',
    };
  }
  if (['bounced', 'complained', 'failed', 'blocked', 'error', 'delivery_delayed'].includes(s) || activity.problem_type === 'bounced_failed') {
    return {
      what: `${activity.vendor_name || 'Vendor'} — delivery issue`,
      why: 'The vendor email bounced or failed — they did not receive the order.',
      actionLabel: 'Fix & resend',
    };
  }
  return {
    what: `${activity.vendor_name || 'Order'} needs attention`,
    why: 'This purchase has not been opened by the vendor.',
    actionLabel: 'Follow up',
  };
}

export function formatMoney(amount) {
  const n = toNumber(amount);
  if (n === 0) return '$0.00';
  return `$${n.toFixed(2)}`;
}

export function amountLabel(amount, kind) {
  const kindLabel = kind === AMOUNT_KIND.ESTIMATED ? 'estimated' : kind === AMOUNT_KIND.ORDERED ? 'ordered' : kind === AMOUNT_KIND.ACTUAL ? 'actual' : 'verified';
  return `${formatMoney(amount)} ${kindLabel}`;
}
